'use client';

import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";

/**
 * @typedef {import("@prismicio/client").Content.TextSlice} TextSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<TextSlice>} TextProps
 * @type {import("react").FC<TextProps>}
 */
const Text = ({ slice }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  // Vérifier si le titre est un h1
  const isH1 = slice.primary.title?.[0]?.type === 'heading1';

  useEffect(() => {
    if (!isInView || isTyping || !isH1) return;

    // Extraire le texte du strong uniquement
    const strongNode = slice.primary.title?.[0]?.spans?.find(span => span.type === 'strong');
    if (!strongNode) return;

    const fullText = slice.primary.title[0].text;
    const strongText = fullText.slice(strongNode.start, strongNode.end);

    // Délai de 300ms avant de commencer l'animation
    setTimeout(() => {
      setIsTyping(true);
      let currentIndex = 0;

      const interval = setInterval(() => {
        if (currentIndex <= strongText.length) {
          setDisplayedText(strongText.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(interval);
          setIsTyping(false);
        }
      }, 100);

      return () => clearInterval(interval);
    }, 300);
  }, [isInView, slice.primary.title, isH1]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pt-10 md:pt-12 mb-20"
    >
      <div className="max-w-400 mx-auto w-full px-8 md:px-16 flex flex-col gap-3" ref={ref}>
        {isFilled.richText(slice.primary.title) && (
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading1: ({ children }) => <h1 className="uppercase">{children}</h1>,
              heading2: ({ children }) => <h2>{children}</h2>,
              heading3: ({ children }) => <h3>{children}</h3>,
              strong: ({ children }) => (
                <strong className={isH1 ? "relative" : ""}>
                  {isH1 && <span className="invisible">{children}</span>}
                  <span className={isH1 ? "absolute inset-0" : ""}>
                    {isH1 ? (displayedText || "") : children}
                  </span>
                </strong>
              ),
            }}
          />
        )}
        <div className={`flex flex-col gap-4 ${slice.variation === "default" ? 'max-w-150' : ''}`}>
          {isFilled.richText(slice.primary.text) && (
            <PrismicRichText
              field={slice.primary.text}
              components={{
                paragraph: ({ children }) => <p className={`${slice.variation === "default" ? '' : 'mt-2'}`}>{children}</p>,
                heading2: ({ children }) => <h2 className={`${slice.variation === "default" ? '' : 'mt-6 mb-3'}`}>{children}</h2>,
                heading3: ({ children }) => <h3 className={`${slice.variation === "default" ? '' : 'mt-6 mb-3'}`}>{children}</h3>,
                heading4: ({ children }) => <h4 className={`${slice.variation === "default" ? '' : 'mt-6 mb-3'}`}>{children}</h4>,
                strong: ({ children }) => <strong>{children}</strong>,
                em: ({ children }) => <em>{children}</em>,
                hyperlink: ({ node, children }) => (
                  <a href={node.data.url} className="a-underline" target={node.data.target || "_self"} rel={node.data.target === "_blank" ? "noopener noreferrer" : undefined}>
                    {children}
                  </a>
                ),
              }}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default Text;
